import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, RefreshCw } from "lucide-react";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { QUOTATION_MODULES_URL, QUOTATION_MODULE_URL } from "../../constants/api";

const CHARGE_TYPES = [
  { value: "included_only", label: "Included (bundle only)" },
  { value: "one_time",      label: "One-time add-on" },
  { value: "recurring",     label: "Recurring add-on" },
  { value: "per_unit",      label: "Per-unit add-on" },
  { value: "hourly",        label: "Per-hour service" },
];

const CATALOG_TYPES = [
  { value: "module",  label: "Module"  },
  { value: "feature", label: "Feature" },
  { value: "addon",   label: "Add-on"  },
];

const CATALOG_BADGE = {
  module:  "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  feature: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  addon:   "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
};

const CATALOG_TABS = [
  { key: "all",     label: "All"      },
  { key: "module",  label: "Modules"  },
  { key: "feature", label: "Features" },
  { key: "addon",   label: "Add-ons"  },
];

const EMPTY = {
  name: "", description: "",
  charge_type: "included_only",
  catalog_type: "module",
  is_available: true,
  price: "", unit_label: "",
  default_gst_rate: 18, txn_per_visit: 0, sort_order: 0,
};

export default function ModulesMaster() {
  const { fetch, create, update, remove } = useApiMethods();
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState("");
  const [catalogFilter, setCatalogFilter] = useState("all");
  const [availFilter, setAvailFilter]     = useState("all"); // all | available | roadmap
  const [form, setForm]       = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm]   = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await fetch(QUOTATION_MODULES_URL);
    if (res?.status === "success") setRows(res.data || []);
    else ToastMsg(res?.message || "Failed to load", "error");
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (row) => {
    setForm({
      name: row.name, description: row.description || "",
      charge_type: row.charge_type,
      catalog_type: row.catalog_type || "module",
      is_available: row.is_available !== false,
      price: row.price,
      unit_label: row.unit_label || "",
      default_gst_rate: row.default_gst_rate,
      txn_per_visit: row.txn_per_visit ?? 0,
      sort_order: row.sort_order,
    });
    setEditingId(row.id); setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return ToastMsg("Name is required", "error");
    const payload = {
      ...form,
      price: form.price || 0,
      txn_per_visit: form.txn_per_visit || 0,
      sort_order: form.sort_order || 0,
    };
    const res = editingId
      ? await update(QUOTATION_MODULE_URL(editingId), payload)
      : await create(QUOTATION_MODULES_URL, payload);
    if (res?.status === "success") {
      ToastMsg(`Module ${editingId ? "updated" : "created"}`, "success");
      setShowForm(false); load();
    } else ToastMsg(res?.message || "Failed", "error");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this module?")) return;
    const res = await remove(QUOTATION_MODULE_URL(id));
    if (res?.status === "success") { ToastMsg("Deleted", "success"); load(); }
    else ToastMsg(res?.message || "Failed", "error");
  };

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(r => {
    if (catalogFilter !== "all" && (r.catalog_type || "module") !== catalogFilter) return false;
    if (availFilter === "available" && r.is_available === false) return false;
    if (availFilter === "roadmap"   && r.is_available !== false) return false;
    if (q && !r.name.toLowerCase().includes(q) && !(r.description || "").toLowerCase().includes(q)) return false;
    return true;
  });

  const chargeLabel  = (v) => CHARGE_TYPES.find(c => c.value === v)?.label || v;
  const catalogLabel = (v) => CATALOG_TYPES.find(c => c.value === v)?.label || "Module";

  // Counts per catalog type for tab badges
  const counts = {
    all:     rows.length,
    module:  rows.filter(r => (r.catalog_type || "module") === "module").length,
    feature: rows.filter(r => r.catalog_type === "feature").length,
    addon:   rows.filter(r => r.catalog_type === "addon").length,
  };

  return (
    <ContainerCard>
      <div className="px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Modules, Features &amp; Add-Ons</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Master catalog of eMedHub products, included features, and chargeable add-ons used in quotations.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load}><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /></Button>
            <Button size="sm" onClick={openCreate}><Plus className="h-3.5 w-3.5 mr-1" /> New</Button>
          </div>
        </div>

        {/* Catalog type tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
          {CATALOG_TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setCatalogFilter(t.key)}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                catalogFilter === t.key
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              }`}
            >
              {t.label} <span className="ml-1 text-[10px] text-gray-400">({counts[t.key]})</span>
            </button>
          ))}
        </div>

        {/* Search + availability filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-72">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
          </div>
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {[
              { key: "all",       label: "All"      },
              { key: "available", label: "Available" },
              { key: "roadmap",   label: "Roadmap"  },
            ].map(o => (
              <button
                key={o.key}
                onClick={() => setAvailFilter(o.key)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  availFilter === o.key
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-gray-400 ml-auto">{filtered.length} of {rows.length}</div>
        </div>

        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              {editingId ? "Edit Catalog Item" : "New Catalog Item"}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Name <span className="text-red-500">*</span></label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Catalog Type</label>
                <Select className="w-full" value={form.catalog_type}
                  onChange={v => setForm(p => ({ ...p, catalog_type: v }))}
                  options={CATALOG_TYPES} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Charge Type</label>
                <Select className="w-full" value={form.charge_type}
                  onChange={v => setForm(p => ({ ...p, charge_type: v }))}
                  options={CHARGE_TYPES} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Price (₹)</label>
                <Input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Unit Label</label>
                <Input placeholder='e.g. "per user", "per hour"' value={form.unit_label}
                  onChange={e => setForm(p => ({ ...p, unit_label: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">GST %</label>
                <Input type="number" value={form.default_gst_rate}
                  onChange={e => setForm(p => ({ ...p, default_gst_rate: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Txn / OP visit</label>
                <Input type="number" value={form.txn_per_visit}
                  onChange={e => setForm(p => ({ ...p, txn_per_visit: e.target.value }))} />
                <p className="text-[10px] text-gray-400 mt-0.5">Used by pricing calculator</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Availability</label>
                <Select className="w-full" value={form.is_available ? "available" : "roadmap"}
                  onChange={v => setForm(p => ({ ...p, is_available: v === "available" }))}
                  options={[
                    { value: "available", label: "Available (deliverable)" },
                    { value: "roadmap",   label: "Roadmap (coming-soon)"   },
                  ]} />
              </div>
              <div className="md:col-span-3">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Description</label>
                <Textarea rows={2} value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={save}>Save</Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/40 text-left">
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">#</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Name</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Type</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Status</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Charge</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Price</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Unit</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Txn/visit</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-10 text-xs text-gray-400">No items match the current filter</td></tr>
                ) : filtered.map((r, i) => {
                  const ct = r.catalog_type || "module";
                  const roadmap = r.is_available === false;
                  return (
                    <tr key={r.id} className={`hover:bg-gray-50/60 dark:hover:bg-gray-700/20 ${roadmap ? "opacity-60" : ""}`}>
                      <td className="px-3 py-2.5 text-gray-400">{i + 1}</td>
                      <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white">{r.name}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${CATALOG_BADGE[ct]}`}>
                          {catalogLabel(ct)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        {roadmap ? (
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                            Roadmap
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            Available
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{chargeLabel(r.charge_type)}</td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-200">{r.price && Number(r.price) > 0 ? `₹${Number(r.price).toLocaleString("en-IN")}` : "—"}</td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{r.unit_label || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-200 tabular-nums">{r.txn_per_visit || 0}</td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => openEdit(r)} className="p-1 text-primary hover:bg-primary/10 rounded"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => del(r.id)} className="p-1 text-red-500 hover:bg-red-500/10 rounded ml-1"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ContainerCard>
  );
}
